import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Input,
  Tag,
  Modal,
  Form,
  Popconfirm,
  Card,
  Row,
  Col,
  App,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  TeamOutlined,
  LockOutlined,
  PlusOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { deviceAPI } from '../services/api';
import { useAuth } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const IS_CUSTOMER_BUILD = process.env.REACT_APP_BUILD_TARGET === 'customer';
const isTenantAdminRole = IS_CUSTOMER_BUILD ? () => false : (role) => role === 'tenant_admin';

const { Title } = Typography;
const { TextArea } = Input;

const DeviceGroups = () => {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { t } = useTranslation();
  const tenantFeatures = Array.isArray(user?.features)
    ? user.features
    : (Array.isArray(user?.tenant?.features) ? user.tenant.features : []);
  const hasAgent = tenantFeatures.includes('ALLOWTRU_AGENT');
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [viewingGroup, setViewingGroup] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [deviceSearchText, setDeviceSearchText] = useState(''); // Device search text
  const [targetKeys, setTargetKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  // Calculate smart group device count
  const calculateSmartGroupDevices = useCallback((group, allDevices) => {
    if (!allDevices || allDevices.length === 0) {
      return { device_count: 0, device_ids: [] };
    }

    let filteredDevices = [];
    
    // Filter devices by group type
    switch (group.type) {
      case 'smart_all':
        filteredDevices = allDevices;
        break;
      case 'smart_platform':
        if (group.platform_filter) {
          filteredDevices = allDevices.filter(device => 
            device.platform?.toLowerCase() === group.platform_filter.toLowerCase()
          );
        }
        break;
      case 'smart_audit':
        filteredDevices = allDevices.filter(device => 
          device.mode === 'audit' || device.mode === 'monitor'
        );
        break;
      case 'smart_enforce':
        filteredDevices = allDevices.filter(device => 
          device.mode === 'enforce' || device.mode === 'block'
        );
        break;
      default:
        filteredDevices = [];
    }
    
    return {
      device_count: filteredDevices.length,
      device_ids: filteredDevices.map(d => d.id)
    };
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load all groups (including system and custom groups)
      const response = await deviceAPI.getDeviceGroups({
        search: searchText,
      });
      
      let allGroups = [];
      if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          allGroups = response.data.data;
        } else if (Array.isArray(response.data.data.items)) {
          allGroups = response.data.data.items;
        }
      }
      
      // Calculate device count for all groups
      const groupsWithCounts = allGroups.map(group => {
        // For smart groups, dynamically calculate device count
        if (group.is_system && (group.type === 'smart_all' || group.type === 'smart_platform' || 
            group.type === 'smart_audit' || group.type === 'smart_enforce')) {
          const smartGroupData = calculateSmartGroupDevices(group, devices);
          return {
            ...group,
            device_count: smartGroupData.device_count,
            device_ids: smartGroupData.device_ids,
          };
        }
        
        // For manual groups, use device count from backend
        return {
          ...group,
          device_count: group.device_count || 0,
          device_ids: group.device_ids || [],
        };
      });
      
      const visibleGroups = hasAgent
        ? groupsWithCounts
        : groupsWithCounts.filter(group => !(group.is_system && (group.type === 'smart_audit' || group.type === 'smart_enforce')));

      setGroups(visibleGroups);
    } catch (error) {
      console.error('Failed to load groups:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || t('device_groups.message.load_groups_failed', );
      message.error(errorMessage);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [calculateSmartGroupDevices, message, searchText, devices, t, hasAgent]);

  const loadDevices = async () => {
    try {
      const response = await deviceAPI.getDevices();
      let deviceList = [];
      
      if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          deviceList = response.data.data;
        } else if (Array.isArray(response.data.data.items)) {
          deviceList = response.data.data.items;
        }
      }
      
      setDevices(deviceList);
    } catch (error) {
      console.error('Failed to load devices:', error);
      setDevices([]);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Filter group data
  const filteredGroups = groups.filter(group =>
    group.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Filter devices (for device selection when creating groups)
  const filteredDevices = devices.filter(device => {
    if (!deviceSearchText) return true;
    const searchLower = deviceSearchText.toLowerCase();
    return (
      (device.name && device.name.toLowerCase().includes(searchLower)) ||
      (device.serial_number && device.serial_number.toLowerCase().includes(searchLower)) ||
      (device.uuid && device.uuid.toLowerCase().includes(searchLower)) ||
      (device.user_name && device.user_name.toLowerCase().includes(searchLower)) ||
      (device.platform && device.platform.toLowerCase().includes(searchLower))
    );
  });

  const handleSearch = () => {
    loadGroups();
  };

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    setTargetKeys([]);
    setModalVisible(true);
  };

  const handleView = (group) => {
    setViewingGroup(group);
    setViewModalVisible(true);
  };

  const handleEdit = (group) => {
    if (group.is_system) {
      message.warning(t('device_groups.message.system_group_not_editable', ));
      return;
    }
    
    setEditingGroup(group);
    form.setFieldsValue(group);
    setTargetKeys(group.device_ids || []);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    const group = groups.find(g => g.id === id);
    if (group && group.is_system) {
      message.error(t('device_groups.message.system_group_not_deletable', ));
      return;
    }
    try {
      await deviceAPI.deleteDeviceGroup(id);
      message.success(t('device_groups.message.delete_success', ));
      loadGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      if (errorMessage?.includes('设备') || errorMessage?.includes('device')) {
        message.error(t('device_groups.delete.error_remove_devices_first', ));
      } else if (errorMessage?.includes('策略') || errorMessage?.includes('policy')) {
        message.error(t('device_groups.delete.error_remove_policies_first', ));
      } else {
        message.error(t('device_groups.delete.error_generic', { reason: errorMessage }));
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      const groupData = {
        ...values,
        device_ids: targetKeys,
      };
      if (editingGroup) {
        await deviceAPI.updateDeviceGroup(editingGroup.id, groupData);
        message.success(t('device_groups.message.update_success', ));
      } else {
        await deviceAPI.createDeviceGroup(groupData);
        message.success(t('device_groups.message.create_success', ));
      }
      setModalVisible(false);
      loadGroups();
    } catch (error) {
      console.error('Failed to save group:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || (editingGroup ? t('device_groups.message.update_failed', ) : t('device_groups.message.create_failed', ));
      message.error(errorMessage);
    }
  };

  // Export group device information
  const handleExportGroup = (group) => {
    try {
      // Get devices in group
      let groupDevices = [];
      if (group.is_system) {
        // Smart groups filter devices by type
        if (group.type === 'smart_all') {
          groupDevices = devices;
        } else if (group.type === 'smart_platform' && group.platform_filter) {
          groupDevices = devices.filter(device => 
            device.platform?.toLowerCase() === group.platform_filter.toLowerCase()
          );
        } else if (group.type === 'smart_audit') {
          groupDevices = devices.filter(device => 
            device.mode === 'audit' || device.mode === 'monitor'
          );
        } else if (group.type === 'smart_enforce') {
          groupDevices = devices.filter(device => 
            device.mode === 'enforce' || device.mode === 'block'
          );
        }
      } else {
        // Custom groups filter by device_ids
        groupDevices = devices.filter(device => 
          group.device_ids?.includes(device.id)
        );
      }

      // Prepare export data
      const exportData = groupDevices.map(device => ({
        [t('device_groups.export.headers.group_name', )]: group.name || 'N/A',
        [t('device.export.headers.name', )]: device.name || 'N/A',
        [t('device.export.headers.serial_number', )]: device.serial_number || 'N/A',
        'UUID': device.uuid || 'N/A',
        [t('device.export.headers.user', )]: device.user_name || 'N/A',
        [t('device.export.headers.ip', )]: device.ip_address || 'N/A',
        [t('device.export.headers.os', )]: device.platform || 'N/A',
        [t('device.export.headers.online_status', )]: device.is_online ? t('device.online', ) : t('device.offline', ),
        [t('device.export.headers.last_seen', )]: device.last_seen_at ? formatDate(device.last_seen_at) : 'N/A'
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      const colWidths = [
        { wch: 15 }, // Group name
        { wch: 15 }, // Device name
        { wch: 20 }, // Serial number
        { wch: 25 }, // UUID
        { wch: 15 }, // User
        { wch: 15 }, // IP address
        { wch: 10 }, // Platform
        { wch: 10 }, // Status
        { wch: 20 }  // Last seen
      ];
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, t('device.export.sheet_name', ));
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `${t('device_groups.export.file_name_prefix', )}_${group.name}_${timestamp}.xlsx`;
      XLSX.writeFile(wb, filename);
      message.success(t('device_groups.export.success', { name: group.name, count: exportData.length }));
    } catch (error) {
      console.error('Export failed:', error);
      message.error(t('device.export.failedRetry', ));
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: filteredGroups.length,
    });
  };

  // Search resets pagination
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [searchText]);

  // Translate group names that come from the backend
  const translateGroupName = (groupName) => {
    // Map specific group names to translation keys
    // Note: Group names are the actual names stored in the database
    const groupNameMap = {
      // Smart mode groups
      '执行模式群组': 'device_groups.default_groups.execution_mode_group',
      '审计模式群组': 'device_groups.default_groups.audit_mode_group',
      // Platform groups
      'All': 'device_groups.default_groups.all_devices',
      'macOS': 'device_groups.default_groups.all_macos_devices',
      'Windows': 'device_groups.default_groups.all_windows_devices',
      'Linux': 'device_groups.default_groups.all_linux_devices',
      // Fallback for cases where descriptions might be sent as names
      '所有处于执行模式的设备': 'device_groups.default_groups.all_execution_mode_devices',
      '所有处于审计模式的设备': 'device_groups.default_groups.all_audit_mode_devices',
      '所有Linux设备': 'device_groups.default_groups.all_linux_devices',
      '所有Windows设备': 'device_groups.default_groups.all_windows_devices',
      '所有macOS设备': 'device_groups.default_groups.all_macos_devices',
      '所有设备': 'device_groups.default_groups.all_devices'
    };
    
    // Trim whitespace and normalize the group name for comparison
    const normalizedGroupName = groupName?.trim();
    const translationKey = normalizedGroupName ? groupNameMap[normalizedGroupName] : undefined;
    
    if (translationKey) {
      return t(translationKey);
    }
    
    // For custom groups or unmapped groups, return as is
    return groupName;
  };

  const translateGroupDescription = (description) => {
    const descriptionMap = {
      '所有处于执行模式的设备': 'device_groups.default_groups.all_execution_mode_devices',
      '所有处于审计模式的设备': 'device_groups.default_groups.all_audit_mode_devices',
      '所有Linux设备': 'device_groups.default_groups.all_linux_devices',
      '所有Windows设备': 'device_groups.default_groups.all_windows_devices',
      '所有macOS设备': 'device_groups.default_groups.all_macos_devices',
      '所有设备': 'device_groups.default_groups.all_devices',
    };

    const normalizedDescription = description?.trim();
    const translationKey = normalizedDescription ? descriptionMap[normalizedDescription] : undefined;
    if (translationKey) {
      return t(translationKey);
    }
    return description;
  };

  const getGroupTag = (group) => {
    if (group.is_system) {
      const systemGroupColors = {
        'smart_all': 'purple',
        'smart_platform': {
          'macos': 'purple',
          'windows': 'blue', 
          'linux': 'green',
          'default': 'geekblue'
        },
        'smart_audit': 'orange',
        'smart_enforce': 'red',
        'default': 'gold'
      };
      let color = 'gold';
      let label = t('device_groups.tag.smart_group', );
      if (group.type === 'smart_all') {
        color = systemGroupColors.smart_all;
        label = t('device_groups.tag.all_devices', );
      } else if (group.type === 'smart_platform') {
        const platformKey = (group.platform_filter || '').toLowerCase();
        color = systemGroupColors.smart_platform[platformKey] || systemGroupColors.smart_platform.default;
        const platformLabelMap = {
          macos: 'macOS',
          windows: 'Windows',
          linux: 'Linux',
        };
        const platformLabel = platformLabelMap[platformKey] || t('device.platform.generic', );
        label = t('device_groups.tag.platform_devices', { platform: platformLabel });
      } else if (group.type === 'smart_audit') {
        color = systemGroupColors.smart_audit;
        label = t('device_groups.tag.audit_mode', );
      } else if (group.type === 'smart_enforce') {
        color = systemGroupColors.smart_enforce;
        label = t('device_groups.tag.enforce_mode', );
      }
      return (
        <Tag color={color} icon={<LockOutlined />}> {label} </Tag>
      );
    }
    return <Tag color="cyan">{t('dashboard.cards.device_groups.custom', ).replace(':', '')}</Tag>;
  };

  const columns = [
    {
      title: t('device_groups.table.columns.name', ),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Space>
          <span>{translateGroupName(text)}</span>
          {getGroupTag(record)}
        </Space>
      ),
    },
    {
      title: t('device_groups.table.columns.description', ),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text, record) => (record?.is_system ? translateGroupDescription(text) : text),
    },
    {
      title: t('device_groups.table.columns.device_count', ),
      dataIndex: 'device_count',
      key: 'device_count',
      render: (count) => (
        <Tag color="blue" icon={<TeamOutlined />}> {count} {t('device_groups.units.devices', )} </Tag>
      ),
    },
    {
      title: t('device_groups.table.columns.created_at', ),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? formatDate(text) : '-',
    },
    {
      title: t('device_groups.table.columns.actions', ),
      key: 'action',
      render: (_, record) => {
        const userRole = user?.role;
        const canEdit = userRole === 'user' || userRole === 'admin' || isTenantAdminRole(userRole) || userRole === 'super_admin';
        const canDelete = userRole === 'admin' || isTenantAdminRole(userRole) || userRole === 'super_admin';
        const isAuditor = userRole === 'auditor';
        return (
          <Space size="middle">
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)}>
              {t('common.actions.view', )}
            </Button>
            {canEdit && (
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} disabled={record.is_system}>
                {t('common.actions.edit', )}
              </Button>
            )}
            {canDelete && (
              <Popconfirm
                title={t('device_groups.modal.delete_confirm.title', )}
                onConfirm={() => handleDelete(record.id)}
                okText={t('common.actions.confirm', )}
                cancelText={t('common.actions.cancel', )}
                disabled={record.is_system}
              >
                <Button type="link" danger icon={<DeleteOutlined />} disabled={record.is_system}>
                  {t('common.actions.delete', )}
                </Button>
              </Popconfirm>
            )}
            {isAuditor && !canEdit && !canDelete && (
              <span style={{ color: '#999', fontSize: '12px' }}>{t('common.view_only', )}</span>
            )}
          </Space>
        );
      },
    },
  ];

  // Device selection table row selection config
  const rowSelection = {
    selectedRowKeys: targetKeys,
    onChange: (selectedRowKeys) => {
      setTargetKeys(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      name: record.name,
    }),
  };

  // Device selection table column definitions
  const deviceSelectionColumns = [
    {
      title: t('device.export.headers.name', ),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => <strong>{text}</strong>,
      ellipsis: false,
    },
    {
      title: t('device.export.headers.serial_number', ),
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 120,
      render: (text) => (
        <span title={text || 'N/A'}>
          {text || 'N/A'}
        </span>
      ),
      ellipsis: false,
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 120,
      render: (text) => (
        <span title={text || 'N/A'}>
          {text || 'N/A'}
        </span>
      ),
      ellipsis: false,
    },
    {
      title: t('device.export.headers.user', ),
      dataIndex: 'user_name',
      key: 'user_name',
      width: 100,
      render: (text) => (
        <span title={text || 'N/A'}>
          {text || 'N/A'}
        </span>
      ),
      ellipsis: false,
    },
    {
      title: t('device.export.headers.ip', ),
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (text) => (
        <span title={text || 'N/A'}>
          {text || 'N/A'}
        </span>
      ),
      ellipsis: false,
    },
    {
      title: t('device.export.headers.os', ),
      dataIndex: 'platform',
      key: 'platform',
      width: 80,
      render: (text) => (
        <span title={text || 'N/A'}>
          {text || 'N/A'}
        </span>
      ),
      ellipsis: false,
    },
    {
      title: t('device.export.headers.online_status', ),
      dataIndex: 'is_online',
      key: 'is_online',
      width: 80,
      render: (isOnline) => (
        <Tag color={isOnline ? 'green' : 'red'}>
          {isOnline ? t('device.online', ) : t('device.offline', )}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>{t('navbar.menu.device_groups', )}</Title>
          </Col>
          <Col>
            <Space>
              <Input
                placeholder={t('device_groups.search.placeholder', )}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                onPressEnter={handleSearch}
                style={{ width: 200 }}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { loadDevices(); loadGroups(); }}
              >
                {t('common.actions.refresh', )}
              </Button>
              {(user?.role === 'user' || user?.role === 'admin' || isTenantAdminRole(user?.role) || user?.role === 'super_admin') && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                  {t('device_groups.actions.create', )}
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredGroups}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            ...pagination,
            total: filteredGroups.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('device_groups.pagination.total_groups', { start: range[0], end: range[1], total }),
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Group edit modal */}
      <Modal
        title={editingGroup ? t('device_groups.modal.edit_title', ) : t('device_groups.modal.create_title', )}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={t('device_groups.form.name.label', )}
            rules={[{ required: true, message: t('device_groups.form.name.required', ) }]}
          >
            <Input placeholder={t('device_groups.form.name.placeholder', )}/>
          </Form.Item>

          <Form.Item
            name="description"
            label={t('device_groups.form.description.label', )}
            rules={[{ required: true, message: t('device_groups.form.description.required', ) }]}
          >
            <TextArea rows={3} placeholder={t('device_groups.form.description.placeholder', )}/>
          </Form.Item>

          <Form.Item label={t('device_groups.form.select_devices', )}>
            <div style={{ marginBottom: 8 }}>
              <span>{t('device_groups.form.selected_count', { count: targetKeys.length })}</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Input
                placeholder={t('device_groups.search.devices_placeholder', )}
                value={deviceSearchText}
                onChange={(e) => setDeviceSearchText(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                style={{ width: '100%' }}
              />
            </div>
            <Table
              rowSelection={rowSelection}
              dataSource={filteredDevices}
              columns={deviceSelectionColumns}
              rowKey="id"
              pagination={{
                pageSize: 8,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) => t('dashboard.pagination.show_total', { start: range[0], end: range[1], total }),
              }}
              scroll={{ x: 'max-content', y: 300 }}
              size="small"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                {t('common.actions.cancel', )}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingGroup ? t('common.actions.update', ) : t('common.actions.create', )}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View group devices modal */}
      <Modal
        title={t('device_groups.modal.view_title', { name: viewingGroup ? translateGroupName(viewingGroup.name) : '' })}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="export" icon={<DownloadOutlined />} onClick={() => handleExportGroup(viewingGroup)}>
            {t('device_groups.modal.actions.export', )}
          </Button>,
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            {t('common.actions.close', )}
          </Button>
        ]}
        width={800}
      >
        {viewingGroup && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div className="flex items-center gap-2">
                  <strong>{t('device_groups.view.group_name', )}</strong>{translateGroupName(viewingGroup.name)}
                  {getGroupTag(viewingGroup)}
                </div>
                <div><strong>{t('device_groups.view.description', )}</strong>{viewingGroup.is_system ? translateGroupDescription(viewingGroup.description) : viewingGroup.description}</div>
                <div><strong>{t('device_groups.view.device_count', )}</strong>{viewingGroup.device_count} {t('device_groups.units.devices', )}</div>
                <div><strong>{t('device_groups.view.created_at', )}</strong>{viewingGroup.created_at ? formatDate(viewingGroup.created_at) : '-'}</div>
              </Space>
            </div>
            <div style={{ marginTop: 24 }}>
              <Title level={5}>{t('device_groups.view.device_list', )}</Title>
              <Table
                size="small"
                dataSource={(devices || []).filter(device => {
                  if (viewingGroup.is_default) {
                    if (viewingGroup.platform_filter) {
                      return device.platform?.toLowerCase() === viewingGroup.platform_filter.toLowerCase();
                    } else {
                      return true; // All devices
                    }
                  } else {
                    return viewingGroup.device_ids?.includes(device.id);
                  }
                })}
                rowKey="id"
                pagination={{
                  pageSize: 5,
                  showSizeChanger: false,
                  showQuickJumper: false,
                }}
                columns={[
                  {
                    title: t('device.export.headers.name', ),
                    dataIndex: 'name',
                    key: 'name',
                    render: (text) => <strong>{text}</strong>,
                  },
                  {
                    title: t('device.export.headers.serial_number', ),
                    dataIndex: 'serial_number',
                    key: 'serial_number',
                    render: (text) => text || 'N/A',
                  },
                  {
                    title: 'UUID',
                    dataIndex: 'uuid',
                    key: 'uuid',
                    render: (text) => text ? text.substring(0, 8) + '...' : 'N/A',
                  },
                  {
                    title: t('device.export.headers.user', ),
                    dataIndex: 'user_name',
                    key: 'user_name',
                    render: (text) => text || 'N/A',
                  },
                  {
                    title: t('device.export.headers.ip', ),
                    dataIndex: 'ip_address',
                    key: 'ip_address',
                    render: (text) => text || 'N/A',
                  },
                  {
                    title: t('device.export.headers.os', ),
                    dataIndex: 'platform',
                    key: 'platform',
                    render: (platform) => (
                      <Tag color={platform === 'windows' ? 'blue' : platform === 'macos' ? 'green' : 'orange'}>
                        {platform?.toUpperCase() || 'N/A'}
                      </Tag>
                    ),
                  },
                  {
                    title: t('device.export.headers.online_status', ),
                    key: 'status',
                    render: (_, record) => {
                      const isOnline = !!record.is_online;
                      const rawDeviceStatus = typeof record.status === 'string' ? record.status : '';
                      const normalizedDeviceStatus = rawDeviceStatus.trim().toLowerCase();
                      const showDeviceStatusTag = ['active', 'pending', 'rejected', 'inactive', 'suspended'].includes(normalizedDeviceStatus);
                      return (
                        <div>
                          <Tag color={isOnline ? 'green' : 'red'}>
                            {isOnline ? t('device.online', ) : t('device.offline', )}
                          </Tag>
                          {showDeviceStatusTag && (
                            <Tag
                              color={
                                normalizedDeviceStatus === 'active'
                                  ? 'blue'
                                  : normalizedDeviceStatus === 'pending'
                                    ? 'orange'
                                    : normalizedDeviceStatus === 'rejected'
                                      ? 'red'
                                      : 'default'
                              }
                              style={{ marginTop: '2px' }}
                            >
                              {normalizedDeviceStatus === 'active'
                                ? t('device.status.active', )
                                : normalizedDeviceStatus === 'pending'
                                  ? t('device.status.pending', )
                                  : normalizedDeviceStatus === 'rejected'
                                    ? t('device.status.rejected', )
                                    : normalizedDeviceStatus === 'inactive'
                                      ? t('device.status.inactive', )
                                      : normalizedDeviceStatus === 'suspended'
                                        ? t('device.status.suspended', )
                                        : rawDeviceStatus}
                            </Tag>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: t('device.export.headers.last_seen', ),
                    dataIndex: 'last_seen_at',
                    key: 'last_seen_at',
                    render: (time) => time ? formatDate(time) : '-',
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DeviceGroups;
